import Controller from '@ember/controller';
import DS from 'ember-data';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import { isEmpty } from '@ember/utils';
import FormValidations from 'cio/utils/form-validations';
import AnalyticsService from 'cio/services/analytics';
import TagModel from 'cio/models/tag';
import FacebookLeadAdsService from 'cio/services/facebook-lead-ads';
import TagsService from 'cio/services/tags';
import CampaignModel from 'cio/models/campaign';
import RouterService from '@ember/routing/router-service';

const Validations = {
  name(value: string) {
    if (isEmpty(value) || isEmpty(value.trim())) {
      return "Name can't be blank";
    }

    return true;
  },
};

async function updateSetupGuideCreatedCampaignStep(
  ctx: { store: DS.Store; analytics: AnalyticsService },
  campaign: CampaignModel,
) {
  let setupGuide = await ctx.store.findRecord('setup-guide', 'current');

  if (setupGuide.completedSteps['create-campaign']) {
    return;
  }

  await setupGuide.assignFirstMessage(campaign);
  ctx.analytics.track('campaign', 'create_started', { source: 'Campaigns' });

  return setupGuide.save();
}

export async function createCampaign(ctx: { store: DS.Store; analytics: AnalyticsService }, model: CampaignModel) {
  await model.saveWithoutType();

  // update setup guide "created campaign" step if not completed already
  // non-critical operation: launched in the background
  updateSetupGuideCreatedCampaignStep(ctx, model);
}

export default class NewController extends Controller {
  @service analytics!: AnalyticsService;
  @service facebookLeadAds!: FacebookLeadAdsService;
  @service tags!: TagsService;
  @service store!: DS.Store;
  @service router!: RouterService;

  declare model: CampaignModel;

  queryParams = ['triggerFormId', 'formType'];

  triggerFormId: string | null = null;
  formType: string | null = null;
  _hasSubmitted = false;

  validations!: FormValidations;

  constructor() {
    super(...arguments);

    this.set('validations', FormValidations.create({ validations: Validations, context: this }));
  }

  _createCampaign = task({ drop: true }, async (evt: Event) => {
    evt.preventDefault();

    this.set('_hasSubmitted', true);

    try {
      await this.validations.validateModel();
    } catch (e) {
      // `validateModel` found and set validation errors
      // return early to avoid executing real submit logic
      return;
    }

    await createCampaign(this, this.model);

    this.router.transitionTo('campaigns.campaign.setup.trigger', this.model, {
      queryParams: { triggerFormId: this.triggerFormId, formType: this.formType },
    });
  });

  async _updateSetupGuideCreatedCampaignStep() {
    let setupGuide = await this.store.findRecord('setup-guide', 'current');

    if (setupGuide.completedSteps['create-campaign']) {
      return;
    }

    await setupGuide.assignFirstMessage(this.model);
    this.analytics.track('campaign', 'create_started', { source: 'Campaigns' });

    return setupGuide.save();
  }

  setDefaults() {
    this.set('_hasSubmitted', false);
  }

  @action
  addTag(tag: TagModel) {
    this.model.set('tags', [...(this.model.tags.content as TagModel[]), tag]);
  }

  @action
  removeTag(tag: TagModel) {
    this.model.set(
      'tags',
      (this.model.tags.content as TagModel[]).filter((t) => t !== tag),
    );
  }

  @action
  onNameChange(name: string) {
    this.model.set('name', name);
    this.validations.validateAttribute('name');
  }
}
